/**
 * LiveSessionsPage — /parent/live-sessions (ROUTES.PARENT_LIVE_SESSIONS).
 *
 * Implements TS-007 T9 (US-007, AC-TS007.4): lists every currently-active English
 * tutoring session across the parent's children (Math Coach excluded, <=1 per
 * child), each rendered as an endable live card. Opened from the Overview tab's
 * "View all live sessions" link, shown only when liveSessions.length > 1.
 *
 * Reuses InProgressSessionBanner (T8) AS-IS for each card: LiveSession (this
 * endpoint's shape) is a structural superset of InProgressSession (childDisplayName,
 * startedAt, questionProgress, sessionId — all present, same types), so passing a
 * LiveSession directly as the `session` prop reuses the exact same End action +
 * EndSessionConfirmDialog + useEndSession flow with zero duplicated card markup
 * (CCM-003 same_behavior: both live surfaces share one identical end flow).
 *
 * Data: dedicated GET /parent/live-sessions (API-002) via getLiveSessions (T2),
 * React Query key ['parent','live-sessions'] — separate from ['parent','dashboard']
 * (the Overview reads its own embedded liveSessions[] from that cache instead, so
 * visiting this page never causes an extra dashboard round-trip).
 *
 * TO-LL-036: fetch errors are never swallowed — surfaced via ErrorState with a
 * retry action wired to the query's refetch().
 * BR-TS007.1: no placeholder — every state (loading/error/empty/success) renders
 * real, working content; the 0/1-session edge case degrades gracefully rather than
 * crashing (story edge case).
 *
 * Route registration: App.tsx, inside the existing <ParentGuard> block (CCM-03).
 */

import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getLiveSessions } from '@/services/sessions';
import { LoadingState } from '@/components/LoadingState';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { InProgressSessionBanner } from '@/components/InProgressSessionBanner';
import { AppLogo } from '@/components/AppLogo';
import { LogoutButton } from '@/components/LogoutButton';
import { ROUTES } from '@/constants/routes';
import { ArrowLeft } from 'lucide-react';
import type { LiveSession } from '@/types/coins';

export function LiveSessionsPage() {
  const navigate = useNavigate();

  const {
    data: liveSessions,
    isLoading,
    error,
    refetch,
  } = useQuery<LiveSession[], Error>({
    queryKey: ['parent', 'live-sessions'],
    queryFn: () => getLiveSessions(),
    staleTime: 30000, // 30 seconds — consistent with useParentDashboard/useParentSessions
  });

  return (
    <div className="otter-bg font-body min-h-screen text-otter-earth-800">
      {/* Page chrome — reuses the TopupCoinDetailPage header pattern (AppLogo + LogoutButton) */}
      <header className="sticky top-0 z-20 flex items-center justify-between border-b border-otter-sand bg-otter-paper/80 px-3 py-2 backdrop-blur-md md:px-6 md:py-3">
        <AppLogo />
        <LogoutButton />
      </header>

      <main className="mx-auto max-w-3xl px-4 pb-8 pt-4 md:px-6 md:pt-7">
        <button
          type="button"
          onClick={() => navigate(ROUTES.PARENT_DASHBOARD)}
          className="mb-4 inline-flex h-11 items-center gap-1.5 rounded-lg px-2 text-sm font-semibold text-pebbles-500 transition-colors hover:bg-otter-sand/40 hover:text-otter-earth-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-otter-orange-400"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Back to dashboard
        </button>

        <h1 className="font-display mb-1 text-2xl font-semibold leading-tight text-otter-earth-800 md:text-[32px]">
          Live sessions
        </h1>
        <p className="mb-6 text-sm font-medium text-gray-500 md:text-[15px]">
          Every child currently in an English tutoring session.
        </p>

        {/* Loading state */}
        {isLoading && <LoadingState message="Loading live sessions..." />}

        {/* Error state — never swallowed (TO-LL-036) */}
        {!isLoading && error && (
          <ErrorState
            title="Could not load live sessions"
            message="Couldn't load live sessions. Please try again."
            onRetry={refetch}
          />
        )}

        {/* Empty state — including when this route is reached directly with zero
            live sessions (story edge case: degrades gracefully, never crashes). */}
        {!isLoading && !error && (liveSessions?.length ?? 0) === 0 && (
          <EmptyState message="No live sessions right now." />
        )}

        {/* Success state — one endable card per live session. InProgressSessionBanner
            (T8) is reused as-is: LiveSession is structurally compatible with the
            InProgressSession prop it expects, so the End action + confirmation
            dialog + useEndSession flow are identical to the Overview card. */}
        {!isLoading && !error && liveSessions && liveSessions.length > 0 && (
          <div className="grid gap-5 sm:grid-cols-2">
            {liveSessions.map((session) => (
              <InProgressSessionBanner key={session.sessionId} session={session} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
